import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { Alert } from "@/core/components/ui/alert/alert";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent } from "@/core/components/ui/card";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { extractErrorMessage } from "@/core/api/errors";
import { dayjs } from "@/core/utils/date";
import { useSetDocumentsReady } from "./query";

interface SubmittedScreenProps {
  businessId: string;
  businessName: string;
  readyAt: string;
  /** Where they land once they have taken the claim back. */
  onReopened: () => void;
}

// The end of the flow, as its own page: a finished state does not belong as a card on a
// working page. While this is showing, it is the whole of onboarding.
//
// There is no uploader here on purpose. "I have more to send" means they are not finished
// after all, so it takes the claim back and returns them to the steps, rather than leaving
// us building from a set they are still adding to.
export const SubmittedScreen = ({
  businessId,
  businessName,
  readyAt,
  onReopened,
}: SubmittedScreenProps) => {
  const [error, setError] = useState<string | null>(null);
  const readyMutation = useSetDocumentsReady(businessId);

  const reopen = () => {
    setError(null);
    readyMutation.mutate(false, {
      onSuccess: () => onReopened(),
      onError: (failure) => setError(extractErrorMessage(failure).message),
    });
  };

  return (
    <Stack
      fullWidth
      align="center"
      className="min-h-[calc(100vh-67px)] bg-surface-secondary px-4 py-10"
    >
      <Stack gap="lg" align="stretch" fullWidth className="max-w-2xl">
        <Typography variant="titleW2">
          Thanks, that is everything we need to start
        </Typography>

        <Card className="w-full border-stroke-tertiary shadow-sm">
          <CardContent className="p-6 sm:p-8">
            <Stack gap="lg" align="stretch" fullWidth>
              {/* The check belongs to the date line, not to the whole block, so it is
                  sized to that line and this section reads like the two below it. */}
              <Stack gap="xs" fullWidth>
                <Stack direction="row" gap="sm" align="center">
                  <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-60" />
                  <Typography variant="subtitleW2">
                    Sent for review on {dayjs(readyAt).format("MMMM D, YYYY")}
                  </Typography>
                </Stack>
                <Typography variant="bodyW2" textColor="secondary">
                  We are going through your documents and building a first
                  version of payroll for {businessName}. That usually takes a
                  few days.
                </Typography>
              </Stack>

              <Stack gap="xs" fullWidth>
                <Typography variant="subtitleW3">
                  We may come back with questions
                </Typography>
                <Typography variant="bodyW2" textColor="secondary">
                  If something is missing, or we cannot follow how a number was
                  worked out, we will ask you for more. That is normal on a
                  first pass, and it is quicker than us guessing at a rule.
                </Typography>
              </Stack>

              <Stack gap="xs" fullWidth>
                <Typography variant="subtitleW3">
                  This page updates on its own
                </Typography>
                <Typography variant="bodyW2" textColor="secondary">
                  When your payroll is ready you will see it here, with every
                  number worked out for each person. There is nothing else you
                  need to do in the meantime.
                </Typography>
              </Stack>

              <Separator />

              <Stack gap="sm" align="stretch" fullWidth>
                <Typography variant="captionW1" textColor="tertiary">
                  Found something else since you sent it? We will hold off
                  building until you tell us you are finished again.
                </Typography>
                {error && <Alert variant="destructive">{error}</Alert>}
                <Stack direction="row" fullWidth>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={readyMutation.isPending}
                    disabled={readyMutation.isPending}
                    onClick={reopen}
                  >
                    I have more to send
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
};
